import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Play, Square } from 'lucide-react';
import { format } from 'date-fns';

interface SignalEvent {
  id: string;
  timestamp: string;
  instrument: string;
  action: string;
  action_type: string;
  amount: number | null;
  win: boolean | null;
  bot_name: string;
  raw_payload: any;
}

interface SignalProcessingTabProps {
  filters: {
    botId?: string;
    instrument?: string;
    dateFrom: string;
    dateTo: string;
  };
  refreshKey: number;
  bots: { id: string; name: string; signal_token: string }[];
}

export function SignalProcessingTab({ filters, refreshKey, bots }: SignalProcessingTabProps) {
  const [signals, setSignals] = useState<SignalEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 50,
    total: 0,
  });

  useEffect(() => {
    fetchSignals();
  }, [filters, refreshKey, pagination.page]);

  const fetchSignals = async () => {
    setIsLoading(true);
    try {
      let query = supabase
        .from('bot_signal_events')
        .select(`
          id,
          timestamp,
          instrument,
          action,
          action_type,
          amount,
          win,
          raw_payload,
          bots!bot_id(name)
        `, { count: 'exact' })
        .gte('timestamp', `${filters.dateFrom}T00:00:00Z`)
        .lte('timestamp', `${filters.dateTo}T23:59:59Z`)
        .order('timestamp', { ascending: false })
        .range((pagination.page - 1) * pagination.pageSize, pagination.page * pagination.pageSize - 1);

      if (filters.botId) {
        query = query.eq('bot_id', filters.botId);
      }

      if (filters.instrument) {
        query = query.eq('instrument', filters.instrument);
      }

      const { data, error, count } = await query;

      if (error) {
        console.error('Error fetching signals:', error);
        return;
      }

      const signalsData = (data || []).map(signal => ({
        id: signal.id,
        timestamp: signal.timestamp,
        instrument: signal.instrument,
        action: signal.action,
        action_type: signal.action_type,
        amount: signal.amount,
        win: signal.win,
        bot_name: (signal as any).bots?.name || 'Unknown Bot',
        raw_payload: signal.raw_payload,
      }));

      setSignals(signalsData);
      setPagination(prev => ({ ...prev, total: count || 0 }));

    } catch (error) {
      console.error('Error fetching signals:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getActionTypeBadge = (actionType: string) => {
    const isEnter = actionType === 'ENTER';
    const variant = isEnter ? 'default' : 'secondary';
    const icon = isEnter ? <Play className="h-3 w-3" /> : <Square className="h-3 w-3" />;
    
    return (
      <Badge variant={variant} className="flex items-center gap-1">
        {icon}
        {actionType}
      </Badge>
    );
  };

  const getActionBadge = (action: string) => {
    const isLong = action.includes('LONG');
    const variant = isLong ? 'default' : 'secondary';
    const icon = isLong ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />;
    
    return (
      <Badge variant={variant} className="flex items-center gap-1">
        {icon}
        {action}
      </Badge>
    );
  };

  const getWinBadge = (win: boolean | null) => {
    if (win === null) return null;
    return (
      <Badge variant={win ? 'default' : 'destructive'}>
        {win ? 'WIN' : 'LOSS'}
      </Badge>
    );
  };

  const totalPages = Math.ceil(pagination.total / pagination.pageSize);

  return (
    <div className="space-y-6">
      {/* Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Signal Processing Summary</CardTitle>
          <CardDescription>
            All ENTER and EXIT signals received from TradingView webhooks
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">
            Total signals: {pagination.total} | Showing page {pagination.page} of {totalPages}
          </div>
        </CardContent>
      </Card>

      {/* Signals Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Signals</CardTitle>
          <CardDescription>
            Complete history of all signal processing events
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-muted-foreground">Loading signals...</div>
            </div>
          ) : signals.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-muted-foreground">No signals found for the selected filters</div>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Bot</TableHead>
                    <TableHead>Instrument</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Result</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {signals.map((signal) => (
                    <TableRow key={signal.id}>
                      <TableCell className="font-mono text-sm">
                        {format(new Date(signal.timestamp), 'yyyy-MM-dd HH:mm:ss')}
                      </TableCell>
                      <TableCell className="font-medium">
                        {signal.bot_name}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{signal.instrument}</Badge>
                      </TableCell>
                      <TableCell>
                        {getActionTypeBadge(signal.action_type)}
                      </TableCell>
                      <TableCell>
                        {getActionBadge(signal.action)}
                      </TableCell>
                      <TableCell>
                        {signal.amount ? signal.amount.toString() : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {getWinBadge(signal.win)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground">
                  Showing {((pagination.page - 1) * pagination.pageSize) + 1} to{' '}
                  {Math.min(pagination.page * pagination.pageSize, pagination.total)} of{' '}
                  {pagination.total} results
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <div className="text-sm">
                    Page {pagination.page} of {totalPages}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page >= totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}